import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { getCookie } from '@auth/bp-pf-auth';
import Clarity from '@microsoft/clarity';
import { environment } from '../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ClarityService {
  private router = inject(Router);

  init(): void {
    if (!environment.clarityProjectId) return;

    Clarity.init(environment.clarityProjectId);
    Clarity.setTag('environment', environment.app_id || 'unknown');
    this.identify();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event) => {
      const pageName = this.resolvePageName((event as NavigationEnd).urlAfterRedirects);
      Clarity.setTag('page', pageName);
      // Re-attempt on every navigation: the auth cookies aren't set yet
      // on the very first load, only once the login redirect completes.
      this.identify();
    });
  }

  identify(): void {
    if (!environment.clarityProjectId) return;

    const tenantCode = getCookie(`${environment.app_id}_tenant_code`);
    const email = tenantCode ? getCookie(`${tenantCode}_${environment.app_id}_email`) : null;
    if (!email) return;

    Clarity.identify(email, undefined, undefined, email);
  }

  setTag(key: string, value: string | string[]): void {
    if (!environment.clarityProjectId) return;
    Clarity.setTag(key, value);
  }

  trackEvent(name: string): void {
    if (!environment.clarityProjectId) return;
    Clarity.event(name);
  }

  private resolvePageName(url: string): string {
    const path = url.split('?')[0];
    const segments = path.split('/').filter(Boolean);

    // First segment is the tenant code — skip it
    const pageSegments = segments.slice(1);

    // Strip dynamic IDs: UUIDs and purely numeric segments
    const idPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$|^\d+$/i;
    const meaningful = pageSegments.filter(s => !idPattern.test(s));

    if (!meaningful.length) return 'Home';

    return meaningful
      .map(s => s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
      .join(' > ');
  }
}
